import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./styles.module.scss";

import Header from "components/ui/Header";
import Page from "components/ui/Page";
import BasicButton from "components/forms/BasicButton";
import { getAllProposalFeedback } from "api/Proposals";

/**
 * Every answer prospects have given, across every proposal, in one place.
 *
 * The survey (useful / pricing / follow up) and the contact form both land
 * in the same table; this is the combined view Jack asked for - the
 * proposal page shows one proposal's replies, this shows all of them with
 * the counts at the top, so "how many said pricing was too high" is a
 * glance rather than a query.
 */
const ProposalFeedback = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<any[]>([]);
  const [counts, setCounts] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>("");

  useEffect(() => {
    document.title = `${process.env.REACT_APP_NAME} | Proposal feedback`;
    getAllProposalFeedback().then((res: any) => {
      setLoading(false);
      if (res?.error) return setError(res.error);
      setRows(res.rows ?? []);
      setCounts(res.counts ?? null);
    });
  }, []);

  const proposals = Array.from(new Map(rows.map((r) => [r.proposalId, r.prospectName])).entries());
  const shown = filter ? rows.filter((r) => r.proposalId === filter) : rows;

  const countRow = (label: string, obj: Record<string, number> | undefined) => {
    const entries = Object.entries(obj ?? {});
    if (!entries.length) return null;
    return (
      <div className={styles.field} style={{ minWidth: 200 }}>
        <label>{label}</label>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {entries.map(([k, v]) => (
            <span key={k} className={`${styles.pill} ${styles.info}`}>
              {k}: {v}
            </span>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <Header />
      <Page
        title="Proposal feedback"
        subtitle="What prospects said, across every proposal. Survey answers and start-the-conversation replies."
        titleAction={
          <BasicButton outline scheme="secondary" keepText onClick={() => navigate("/proposals")}>
            All proposals
          </BasicButton>
        }
      >
        <div className={styles.wrap}>
          {error && <div className={styles.err}>{error}</div>}
          {loading && <p className={styles.sub}>Loading…</p>}

          {counts && (
            <div className={styles.row} style={{ flexWrap: "wrap", gap: 18, marginBottom: 18 }}>
              <div className={styles.field} style={{ minWidth: 120 }}>
                <label>Replies</label>
                <div style={{ fontSize: 22, fontWeight: 700 }}>{counts.total}</div>
              </div>
              <div className={styles.field} style={{ minWidth: 120 }}>
                <label>Asked to talk</label>
                <div style={{ fontSize: 22, fontWeight: 700 }}>{counts.contact}</div>
              </div>
              {countRow("Was it useful?", counts.useful)}
              {countRow("Pricing", counts.pricing)}
              {countRow("Follow up?", counts.followup)}
            </div>
          )}

          {proposals.length > 1 && (
            <div className={styles.field} style={{ maxWidth: 360, marginBottom: 14 }}>
              <label>Proposal</label>
              <select value={filter} onChange={(e) => setFilter(e.target.value)}>
                <option value="">All proposals</option>
                {proposals.map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {!loading && shown.length === 0 && <p className={styles.sub}>No replies yet.</p>}

          {shown.length > 0 && (
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Proposal</th>
                  <th>Reply</th>
                  <th>Follow up</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => {
                  const a = r.responses ?? {};
                  const isContact = a.kind === "contact";
                  return (
                    <tr key={r.id} style={{ cursor: "pointer" }} onClick={() => navigate(`/proposals/${r.proposalId}`)}>
                      <td style={{ whiteSpace: "nowrap" }}>{new Date(r.submittedAt).toLocaleString()}</td>
                      <td>{r.prospectName}</td>
                      <td>
                        {isContact ? (
                          <>
                            <b>{a.name}</b> asked to start the conversation
                            <div className={styles.sub} style={{ margin: 0 }}>
                              {[a.email, a.phone].filter(Boolean).join(" · ")}
                              {a.message ? ` — ${a.message}` : ""}
                            </div>
                          </>
                        ) : (
                          <>
                            {a.useful && <span className={`${styles.pill} ${styles.info}`}>useful: {a.useful}</span>}{" "}
                            {a.pricing && <span className={`${styles.pill} ${a.pricing === "Too high" ? styles.warn : styles.info}`}>pricing: {a.pricing}</span>}{" "}
                            {a.followup && <span className={`${styles.pill} ${styles.ok}`}>follow up: {a.followup}</span>}
                          </>
                        )}
                      </td>
                      <td>
                        {r.followUpRequested ? (
                          <span className={`${styles.pill} ${r.acknowledgedAt ? styles.ok : styles.warn}`}>
                            {r.acknowledgedAt ? "handled" : "waiting"}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </Page>
    </>
  );
};

export default ProposalFeedback;
